<template>
    <div>
        <div class="voltage-rise-calculator__header">
            <div>
                <h2 class="h3">System Input</h2>
                <span class="voltage-rise-calculator__tick small semi" :class="{'is-completed' : isCompleted}">
                    {{ completed }}/{{ toComplete }} complete
                    <figure>
                        <svg class="icon">
                            <use xlink:href="#tick" />
                        </svg>
                    </figure>
                </span>
            </div>
            <WarningPrompt
                text="The Voltage Rise tool uses the ‘voltage drop - simplified method’ found in AS/NZS3000:2018 Table C8. The calculation does not take into account power factor or loads. The output of three phase inverters is split evenly across the three phases and a single phase voltage rise calculation is made."
                simple
            />
            <hr>
        </div>

        <div v-if="job.details.noOfPhases">
            <section class="voltage-rise-calculator__section">
                <h3 class="h4">Nominal voltage</h3>
                <NominalVoltage
                    :value="singlePhaseVoltage"
                    @update="updateNominalVoltage"
                />
            </section>

            <section class="voltage-rise-calculator__section">
                <h3 class="h4">Number of phases (electrical installation)</h3>
                <div class="cable-configurator__items-wrapper cable-configurator--full-width">
                    {{ job.details.noOfPhases }} phase
                    <Tooltip text="Number of phases - electrical installation, input on step 1 of the job configuration, determines phases available for the initial point of supply cable run. Update the job configuration if a change is required" />
                </div>
            </section>

            <section class="voltage-rise-calculator__section">
                <h3 class="h4">Cable run</h3>

                <CableConfigurator
                    v-for="(item, index) in supply"
                    :key="item.id"
                    :source="item.source.icon"
                    :source-label="item.source.label"
                    :destination="item.destination.icon"
                    :destination-label="item.destination.label"
                    :cable="item.cable"
                    :previous-cable-phase="index ? supply[(index == 1 ? index - 1 : 1)].cable.phases : false"
                    :is-distribution-board="item.isDistributionBoard"
                    :active="activeItem === item.id"
                    @removeItem="removeDistributionBoard()"
                    @activated="setActive(supply, item.id)"
                    @update="updateDistribution"
                />

                <VoltageRiseAdd
                    :added="additionalSupply"
                    text="Add distribution board"
                    @add="addDistributionBoard"
                />

                <CableConfigurator
                    v-for="item in defaultInverters"
                    :key="item.id"
                    :display-source-dropdown="true"
                    :options="supplySources"
                    :source-label="item.source.label"
                    :destination="item.destination.icon"
                    :destination-label="item.destination.label"
                    :cable="item.cable"
                    :limitation-device="item.limitationDevice"
                    :node="item.node"
                    :destination-power-output="item.destination.powerOutput"
                    :destination-phases="item.destination.phases"
                    :is-pre-existing-system="String(item.isPreExistingSystem) == 'true'"
                    :active="activeItem === item.id"
                    @removeItem="removeInverter()"
                    @activated="setActive(distribution, item.id)"
                    @update="updateDistribution"
                />
            </section>

            <section class="voltage-rise-calculator__section">
                <h3 class="h4">Pre-existing systems</h3>
                <p>If the site already has a system installed it will have an affect on the voltage rise.</p>

                <CableConfigurator
                    v-for="item in preExistingInverters"
                    :key="item.id"
                    :display-source-dropdown="true"
                    :options="supplySources"
                    :source-label="item.source.label"
                    :destination="item.destination.icon"
                    :destination-label="item.destination.label"
                    :cable="item.cable"
                    :node="item.node"
                    :destination-power-output="item.destination.powerOutput"
                    :destination-phases="item.destination.phases"
                    :is-pre-existing-system="item.isPreExistingSystem"
                    :active="activeItem === item.id"
                    @removeItem="removeInverter()"
                    @activated="setActive(distribution, item.id)"
                    @update="updateDistribution"
                />

                <VoltageRiseAdd
                    :added="additionalDistribution"
                    text="Add pre-existing inverter"
                    @add="addExistingInverter"
                />
            </section>

            <WarningPrompt
                v-if="exportLimitationWarning"
                class="mt-xl"
                small
                text="Confirm with your state or territory electrical regulator and the network you are connecting to that export limitation is a permissible method of reducing voltage rise."
            />

            <div class="voltage-rise-calculator__submit submit submit mt-xl mb-xl" :class="{'is-disabled' : !isCompleted}">
                <button class="btn" @click="submit">Next</button>
                <div v-if="loading" class="loading-state">
                    <img src="/dist/images/loader.gif" alt="Loading">
                </div>
                <div v-else-if="error">
                    <p class="small semi text--orange ml-lg">{{ error }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
/* eslint-disable no-console */
import { axios } from '@/../utils';
import { EventBus } from '../../../event-bus';
import CableConfigurator from '@/jobs/calculators/voltage-rise/CableConfigurator';
import VoltageRiseAdd from '@/jobs/calculators/voltage-rise/VoltageRiseAdd';
import NominalVoltage from '@/jobs/calculators/voltage-rise/NominalVoltage';
import WarningPrompt from '@/components/WarningPrompt';
import Tooltip from '@/components/Tooltip';

// import ActiveTrialsTableRowVue from '../../../../../../vendor/craftcms/cms/src/web/assets/pluginstore/src/js/components/modal/steps/cart/ActiveTrialsTableRow.vue';

export default {
    components: {
        CableConfigurator,
        VoltageRiseAdd,
        NominalVoltage,
        WarningPrompt,
        Tooltip
    },

    props:{
        inverters: {
            default: () => []
        },
        cableDiagram: {
            default: null,
        },
        job: {
            default: null
        }
    },

    data() {
        return {
            loading: false,
            activeArray: null,
            activeItem: null,
            error: null,
            additionalSupply: 0,
            additionalDistribution: 0,
            singlePhaseVoltage: null,
            totalValidatedCables: 0,
            exportLimitationWarning: false,
            voltageRiseData: [],
            supply: [
                {
                    id: 'supply_0',
                    node: 1,
                    source: {
                        label: 'POS',
                        icon: 'circle'
                    },
                    destination: {
                        label: 'Meter',
                        icon: 'circle'
                    },
                    supply: false,
                    cable: {
                        distributionCable: true,
                        size: null,
                        length: null,
                        phases: null,
                        electricalInstallation: this.job.details.noOfPhases
                    },
                    isDistributionBoard: false,
                },
                {
                    id: 'supply_1',
                    node: 2,
                    connected: 1,
                    source: {
                        label: 'Meter',
                        icon: 'circle'
                    },
                    destination: {
                        label: 'MSB',
                        icon: 'switchboard'
                    },
                    db: true,
                    supply: true,
                    cable: {
                        distributionCable: true,
                        size: null,
                        length: null,
                        phases: null,
                    },
                    isDistributionBoard: false
                }
            ],
            distribution: []
        };
    },

    computed: {
        supplySources() {
            return this.supply
                .filter(element => String(element.supply) == "true")
                .map((element) => {
                    return { label: element.destination.label, ...element }
                });
        },
        activeConfigurator() {
            if (this.activeArray) {
                return this.activeArray.find((item) => item.id == this.activeItem);
            }
            return null
        },
        toComplete() {
            return this.totalCables.length + 1;
        },
        isCompleted() {
            return this.toComplete === this.completed;
        },
        defaultInverters() {
            return this.distribution.filter(item => String(item.isPreExistingSystem) == 'false');
        },
        preExistingInverters() {
            return this.distribution.filter(item => String(item.isPreExistingSystem) == 'true');
        },
        totalCables() {
            const total = this.supply.concat(this.distribution);
            return total.map(item => item = { ...item.cable });
        },
        completed() {
            if (this.singlePhaseVoltage === null) {
                return this.totalValidatedCables;
            }

            return this.totalValidatedCables + 1;
        },
    },

    watch: {
        totalCables: {
            handler() {
                const cables = this.totalCables;
                const validCables = cables.filter(cable => Object.values(cable).every(field => (field !== null && field !== '')) === true).length;
                this.totalValidatedCables = validCables;
            },

            deep: true
        },
    },

    created() {
        // Determine if the distribution has already been configured
        if (this.cableDiagram) {
            this.supply = this.cableDiagram.supply;
            this.distribution = this.cableDiagram.distribution;
            this.voltageRiseData = this.cableDiagram.voltageRiseData;
            this.singlePhaseVoltage = eval(this.cableDiagram.singlePhaseVoltage);
            return;
        }

        // If not filled out create a new structure
        this.inverters.forEach((element, index) => {
            this.distribution.push({
                id: 'distribution_' + index,
                node: index + 1,
                source: {},
                supplyNode: index + 1,
                destination: {
                    label: element.manufacturer,
                    powerOutput: element.details.nominalOutputPower,
                    phases: element.details.numberOfPhases,
                    icon: 'inverter'
                },
                limitationDevice: {
                    limit: null,
                    location: null
                },
                cable: {
                    size: null,
                    length: null,
                    phases: null,
                },
                isPreExistingSystem: false,
                displaySourceDropdown: true
            })
        });
    },

    mounted() {
        EventBus.$on('show-warning', (value) => this.exportLimitationWarning = value ? true : false);
    },

    methods: {
        addDistributionBoard() {
            this.supply.push({
                id: 'supply_' + this.supply.length,
                node: this.supply.length + 1,
                connected: 2,
                source: {
                    label: 'MSB',
                    icon: 'switchboard'
                },
                destination: {
                    label: `DB${this.supply.length - 1}`,
                    icon: 'distribution'
                },
                db: true,
                supply: true,
                cable: {
                    distributionCable: true,
                    size: null,
                    length: null,
                    phases: null,
                },
                isDistributionBoard: true
            });

            this.additionalSupply = this.additionalSupply + 1;
        },

        addExistingInverter() {
            this.distribution.push({
                id: 'distribution_' + this.distribution.length,
                node: this.distribution.length + 1,
                source: {},
                destination: {
                    label: 'Some inverter',
                    powerOutput: null,
                    phases: 1,
                    icon: 'inverter'
                },
                cable: {
                    length: null,
                    size: null,
                    phases: null,
                },
                isPreExistingSystem: true,
                displaySourceDropdown: true
            });

            this.additionalDistribution = this.additionalDistribution + 1;
        },

        removeDistributionBoard() {
            // Always remove the last distribution board to maintain labeling
            this.supply.splice(this.supply.length - 1, 1)
            //this.supply.splice(index, 1)

            this.additionalSupply = this.additionalSupply - 1;
        },

        removeInverter() {
            // Always remove the last distribution board to maintain labeling
            this.distribution.splice(this.distribution.length - 1, 1)
            //this.supply.splice(index, 1)

            this.additionalDistribution = this.additionalDistribution - 1;
        },

        updateDistribution(source, index) {
            this.distribution[index - 1] = {
                ...this.distribution[index - 1],
                connected: source.node,
                source: source.destination,
            }
        },

        updateNominalVoltage(value) {
            this.singlePhaseVoltage = value;
        },

        setActive(array, item) {
            this.activeArray = array;
            this.activeItem = this.activeItem === item ? null : item;
        },

        async submit() {
                this.loading = true;

                try {
                    const response = await axios.post(`/account/job/${this.job.id}/VoltageRise/datasheet`, {
                        supply: this.supply,
                        distribution: this.distribution,
                        singlePhaseVoltage: this.singlePhaseVoltage,
                        voltageRiseData: this.voltageRiseData,
                        voltRiseCompleted: false
                    })

                    this.loading = false;

                    if (response.data.success) {
                        this.$emit('update:view', 2)
                        this.$emit('update:cable-diagram', {
                            supply: this.supply,
                            distribution: this.distribution,
                            singlePhaseVoltage: this.singlePhaseVoltage,
                            voltageRiseData: this.voltageRiseData,
                            voltRiseCompleted: false
                        })
                    }
                } catch (error) {
                    this.loading = false;
                    this.error = 'A connection issues has occured. Please try again later.';
                }
        },
    }
};
</script>
